import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
} from '@mui/material';
import { Edit as EditIcon } from '@mui/icons-material';
import { getOrders, updateOrderStatus } from '../../firebase/orders';
import { Order } from '../../types';
import { getFunctions, httpsCallable } from 'firebase/functions';
import toast from 'react-hot-toast';

const statusColors = {
  pending: 'warning',
  processing: 'info',
  completed: 'success',
  cancelled: 'error',
} as const;

const OrderManager = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [licenses, setLicenses] = useState<string[][]>([]);

  useEffect(() => {
    loadOrders();
  }, []);

  useEffect(() => {
    if (selectedOrder) {
      // Initialize licenses array based on order items
      setLicenses(selectedOrder.items.map(item => 
        Array(item.quantity).fill('')
      ));
    }
  }, [selectedOrder]);

  const loadOrders = async () => {
    try {
      setLoading(true);
      setError(null);
      const ordersData = await getOrders();
      setOrders(ordersData);
    } catch (error) {
      console.error('Error loading orders:', error);
      setError('Error al cargar los pedidos');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (order: Order) => {
    setSelectedOrder(order);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedOrder(null);
    setLicenses([]);
  };

  const handleStatusChange = async (orderId: string, newStatus: Order['status']) => {
    try {
      setUpdatingStatus(true);
      await updateOrderStatus(orderId, newStatus);
      await loadOrders();
      toast.success('Estado del pedido actualizado');
      handleCloseDialog();
    } catch (error) {
      console.error('Error updating order status:', error);
      toast.error('Error al actualizar el estado del pedido');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleLicenseChange = (productIndex: number, licenseIndex: number, value: string) => {
    const newLicenses = [...licenses];
    newLicenses[productIndex][licenseIndex] = value;
    setLicenses(newLicenses);
  };

  const handleComplete = async () => {
    if (!selectedOrder) return;

    // Validate all licenses are filled
    const hasEmptyLicenses = licenses.some(productLicenses => 
      productLicenses.some(license => !license.trim())
    );

    if (hasEmptyLicenses) {
      toast.error('Por favor, introduce todas las licencias');
      return;
    }

    try {
      setUpdatingStatus(true);
      const functions = getFunctions();
      const sendOrderLicenses = httpsCallable(functions, 'sendOrderLicenses');
      
      await sendOrderLicenses({
        orderId: selectedOrder.id,
        licenses,
      });

      await loadOrders();
      toast.success('Pedido completado y licencias enviadas');
      handleCloseDialog();
    } catch (error) {
      console.error('Error completing order:', error);
      toast.error('Error al completar el pedido');
    } finally {
      setUpdatingStatus(false);
    }
  };

  if (loading) {
    return (
      <Paper sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        Gestionar Pedidos
      </Typography>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID Pedido</TableCell>
              <TableCell>Cliente</TableCell>
              <TableCell>Total</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell>Fecha</TableCell>
              <TableCell align="right">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {orders.map((order) => (
              <TableRow key={order.id}>
                <TableCell>{order.id}</TableCell>
                <TableCell>
                  {order.shippingInfo.firstName} {order.shippingInfo.lastName}
                </TableCell>
                <TableCell>{order.totalAmount.toFixed(2)}€</TableCell>
                <TableCell>
                  <Chip
                    label={order.status}
                    color={statusColors[order.status]}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {order.createdAt.toLocaleDateString()}
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    onClick={() => handleOpenDialog(order)}
                  >
                    <EditIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        {selectedOrder && (
          <>
            <DialogTitle>
              Detalles del Pedido #{selectedOrder.id}
            </DialogTitle>
            <DialogContent>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Información del Cliente
                </Typography>
                <Typography>
                  {selectedOrder.shippingInfo.firstName} {selectedOrder.shippingInfo.lastName}
                </Typography>
                <Typography>{selectedOrder.shippingInfo.email}</Typography>
                <Typography>{selectedOrder.shippingInfo.phone}</Typography>
                <Typography>{selectedOrder.shippingInfo.address}</Typography>
                <Typography>
                  {selectedOrder.shippingInfo.city}, {selectedOrder.shippingInfo.postalCode}
                </Typography>
              </Box>

              <Typography variant="h6" gutterBottom>
                Productos y Licencias
              </Typography>
              {selectedOrder.items.map((item, productIndex) => (
                <Box key={productIndex} sx={{ mb: 4 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="subtitle1">
                      {item.product.name}
                    </Typography>
                    <Typography>
                      Cantidad: {item.quantity}
                    </Typography>
                  </Box>
                  
                  {selectedOrder.status !== 'completed' && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {Array(item.quantity).fill(null).map((_, licenseIndex) => (
                        <TextField
                          key={licenseIndex}
                          fullWidth
                          label={`Licencia ${licenseIndex + 1}`}
                          value={licenses[productIndex]?.[licenseIndex] || ''}
                          onChange={(e) => handleLicenseChange(productIndex, licenseIndex, e.target.value)}
                          size="small"
                        />
                      ))}
                    </Box>
                  )}

                  {selectedOrder.status === 'completed' && item.licenses && (
                    <Box sx={{ mt: 2 }}>
                      {item.licenses.map((license, index) => (
                        <Typography key={index} variant="body2">
                          Licencia {index + 1}: {license}
                        </Typography>
                      ))}
                    </Box>
                  )}
                </Box>
              ))}

              {selectedOrder.status !== 'completed' && (
                <Box sx={{ mt: 3 }}>
                  <FormControl fullWidth>
                    <InputLabel>Estado del Pedido</InputLabel>
                    <Select
                      value={selectedOrder.status}
                      label="Estado del Pedido"
                      onChange={(e) => handleStatusChange(selectedOrder.id, e.target.value as Order['status'])}
                      disabled={updatingStatus}
                    >
                      <MenuItem value="pending">Pendiente</MenuItem>
                      <MenuItem value="processing">En Proceso</MenuItem>
                      <MenuItem value="cancelled">Cancelado</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog}>Cerrar</Button>
              {selectedOrder.status !== 'completed' && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleComplete}
                  disabled={updatingStatus}
                >
                  {updatingStatus ? 'Procesando...' : 'Completar y Enviar Licencias'}
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>
    </Paper>
  );
};

export default OrderManager;